import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {BaseStepComponent} from '../../../../../shared/components/wizard/base-step.component';
import {CompanyWizardService} from '../company-wizard.service';
import {InputText} from 'primeng/inputtext';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {debounceTime, of} from 'rxjs';
import {CompanyStepData} from '../company-wizard.types';
import {Fluid} from 'primeng/fluid';
import {TranslocoDirective} from '@jsverse/transloco';
import {FormFieldComponent} from '../../../../../shared/components/inputs/form-field/form-field.component';
import {FormStepValidationService} from '../../../../../shared/services/form-step-validation.service';

@Component({
  selector: 'app-company-wizard-company-step',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputText,
    Fluid,
    TranslocoDirective,
    FormFieldComponent,
  ],
  templateUrl: './company-wizard-company-step.component.html',
  styleUrl: './company-wizard-company-step.component.scss',
})
export class CompanyWizardCompanyStepComponent
  extends BaseStepComponent<CompanyStepData>
  implements OnInit
{
  override readonly stepIndex = 1;

  private readonly fb = inject(FormBuilder);
  private readonly companyWizardService = inject(CompanyWizardService);
  private readonly formStepValidationService = inject(FormStepValidationService);

  private readonly isTypingSignal = signal(false);
  protected isCheckingSignal = signal(false);

  readonly form = this.fb.group({
    company: this.fb.control('', {validators: Validators.required, updateOn: 'blur'}),
    street: [''],
    zip: [''],
    city: [''],
    website: [''],
  });

  readonly companyControl = this.form.get('company') as FormControl<string>;

  protected readonly isValidComputed = computed(() => {
    const formValid = this.form.valid;
    const isChecking = this.isCheckingSignal();
    const isTyping = this.isTypingSignal();
    return formValid && !isChecking && !isTyping;
  });

  constructor() {
    super();
    this.setupValidation();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeFormData();
  }

  private initializeFormData(): void {
    if (this.formData?.company) {
      this.form.patchValue({
        company: this.formData.company,
        website: this.formData.website,
        street: this.formData.street,
        zip: this.formData.zip,
        city: this.formData.city,
      });
    }
  }

  private setupValidation(): void {
    this.setupControlTouchValidation();
    this.setupTypingIndicator();
    this.setupFormValueChanges();
    this.setupNameExistsCheck();
  }

  private setupControlTouchValidation(): void {
    this.formStepValidationService.setupControlTouchValidation(this.companyControl);
  }

  private setupTypingIndicator(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.isTypingSignal.set(true);
    });

    this.form.valueChanges.pipe(debounceTime(500), takeUntilDestroyed()).subscribe(() => {
      this.isTypingSignal.set(false);
    });
  }

  private setupNameExistsCheck(): void {
    this.formStepValidationService.attributeValueExists(
      this.companyControl,
      this.isCheckingSignal,
      this.checkCompanyName.bind(this),
      this.handleCheckResult.bind(this),
    );
  }

  private checkCompanyName(name: string) {
    const trimmedName = name?.trim();
    if (this.companyWizardService.isEdit() && this.formData?.company === trimmedName) {
      return of({exists: false});
    }
    return trimmedName
      ? this.companyWizardService.checkCompanyNameExists(trimmedName)
      : of({exists: false});
  }

  private handleCheckResult(result: any): void {
    if (result.success) {
      this.updateControlErrors(result.response?.exists);
      this.updateStepData();
    } else {
      this.companyControl.setErrors({
        apiError: 'Unable to verify company name. Please try again later.',
      });
    }

    this.isCheckingSignal.set(false);
  }

  private updateControlErrors(exists: boolean): void {
    this.formStepValidationService.updateControlErrors(this.companyControl, exists);
  }

  private updateStepData(): void {
    const companyName = this.companyControl.value?.trim();
    if (companyName && !this.companyControl.errors) {
      this.companyWizardService.updateStepData({
        stepIndex: this.stepIndex,
        data: {
          id: this.companyWizardService.getStepData<CompanyStepData>(this.stepIndex)?.id,
          company: companyName,
          street: this.form.get('street')?.value,
          zip: this.form.get('zip')?.value,
          city: this.form.get('city')?.value,
          website: this.form.get('website')?.value,
        },
      });
    }
  }

  private setupFormValueChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.form.valid && !this.isCheckingSignal()) {
        this.updateStepData();
      }
    });
  }
}
